import "../../../assets/css/PremiumTools.css";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Copy,
  Download,
  Share2,
  RefreshCw,
  Check,
  Instagram,
  Facebook,
  Linkedin,
  Twitter,
  Zap,
  LayoutTemplate,
  MessageSquare,
  ArrowLeft
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../firebase.init"; 

const SocialMediaPostGenerator = () => {
  // ==========================================
  // 1. STATE MANAGEMENT
  // ==========================================
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // User Data & Credits
  const [credits, setCredits] = useState(0);
  const [apiKeys, setApiKeys] = useState([]);

  // Form State
  const [topic, setTopic] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [tone, setTone] = useState("Professional");
  const [selectedPlatforms, setSelectedPlatforms] = useState({
    instagram: true,
    facebook: true,
    linkedin: true,
    twitter: true,
  });

  // Generated Content State
  const [generatedPosts, setGeneratedPosts] = useState({});

  // Options
  const toneOptions = [
    "Professional",
    "Casual",
    "Friendly",
    "Persuasive",
    "Educational",
    "Witty",
    "Urgent",
  ];

  // ==========================================
  // 2. BACKEND / CREDIT LOGIC (UNCHANGED)
  // ==========================================

  // Fetch API keys
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://allinone-clone-7d5d795e98ce.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        const keys = response.data.data.map((keyObj) => keyObj.key);
        setApiKeys(keys);
        if (keys.length === 0) {
          setError("No API keys available. Please contact administrator.");
        }
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  // Fetch User Data
  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        setLoadingKeys(false);
        return;
      }
      try {
        await fetchApiKeys();
        const userResponse = await axios.get(`https://allinone-clone-7d5d795e98ce.herokuapp.com/users`, {
          params: { userEmail: user?.email },
            
        });
        console.log("User Response:", userResponse.data);
        if (userResponse.data && userResponse.data.length > 0) {
          const userData = userResponse.data[0];
          setCredits(userData.currentBalance);
        } else {
          setError("User data not found");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  // Helper: Get Random API Key
  const getRandomApiKey = () => {
    if (apiKeys.length === 0) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // ==========================================
  // 3. AI GENERATION LOGIC
  // ==========================================

  const constructPrompt = () => {
    const activePlatforms = Object.keys(selectedPlatforms).filter(k => selectedPlatforms[k]);
    
    return `You are an expert Social Media Manager. Create high-engagement social media posts for the following platforms: ${activePlatforms.join(", ")}.

    Topic/Content: ${topic}
    Target Audience: ${targetAudience || "General Audience"}
    Tone: ${tone}

    For EACH platform, follow these rules:
    1. **Hook:** Start with a strong, attention-grabbing first line.
    2. **Body:** Use short, readable sentences. Add spacing for readability.
    3. **Tone:** Adapt the content to fit the specific platform (e.g., LinkedIn = Professional/Value, Instagram = Visual/Casual, Twitter = Concise/Punchy).
    4. **CTA:** Include a natural Call to Action.
    5. **Hashtags:** Include 3-5 relevant hashtags at the bottom.

    **CRITICAL OUTPUT FORMAT:**
    You must separate each post with a specific delimiter. 
    Use exactly these headers: 
    ===INSTAGRAM===
    ===FACEBOOK===
    ===LINKEDIN===
    ===TWITTER===
    
    Only generate sections for the requested platforms. Do not add markdown code blocks (like \`\`\`json). Just raw text separated by headers.`;
  };

  const parseResponse = (text) => {
    const results = {};
    const platforms = ['instagram', 'facebook', 'linkedin', 'twitter'];
    
    platforms.forEach(platform => {
      if (selectedPlatforms[platform]) {
        const regex = new RegExp(`===${platform.toUpperCase()}===([\\s\\S]*?)(?=(===[A-Z]+===|$))`, 'i');
        const match = text.match(regex);
        if (match && match[1]) {
          results[platform] = match[1].trim();
        }
      }
    });
    return results;
  };

  const generatePosts = async () => {
    // Validation
    const activeCount = Object.values(selectedPlatforms).filter(Boolean).length;
    if (!topic.trim()) {
      toast.error("Please enter a topic.");
      return;
    }
    if (activeCount === 0) {
      toast.error("Please select at least one platform.");
      return;
    }
    if (credits < 1) {
      setError("Insufficient credits. You need at least 1 credit.");
      return;
    }

    setGenerating(true);
    setError("");
    setGeneratedPosts({});

    try {
      // 1. Deduct Credit
      const deductResponse = await axios.post(
        `https://allinone-clone-7d5d795e98ce.herokuapp.com/api/deduct-credit`,
        {
          userEmail: user.email,
          amount: 1,
        }
      );

      if (!deductResponse.data.success) {
        throw new Error("Failed to deduct credits");
      }
      setCredits((prev) => prev - 1);

      // 2. Call AI
      const MODEL_ID = "gemini-2.5-flash-lite";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;
      const prompt = constructPrompt();

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: prompt }] }],
          generationConfig: {
            temperature: 0.8,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 2048,
          },
        }),
      });

      if (!response.ok) throw new Error("AI Generation Failed");

      const data = await response.json();
      const generatedText = data.candidates?.[0]?.content?.parts?.[0]?.text;

      if (generatedText) {
        const parsed = parseResponse(generatedText);
        setGeneratedPosts(parsed);
        toast.success("Posts generated successfully!");
      } else {
        throw new Error("Empty response from AI");
      }

    } catch (err) {
      console.error("Generation Error:", err);
      toast.error(err.message);
      setError(err.message);

      // Refund Logic
      try {
        await axios.post(`https://allinone-clone-7d5d795e98ce.herokuapp.com/api/refund-credit`, {
          userEmail: user.email,
          amount: 1,
        });
        setCredits((prev) => prev + 1);
        toast("Credits refunded due to error.");
      } catch (refundErr) {
        console.error("Refund failed:", refundErr);
      }
    } finally {
      setGenerating(false);
    }
  };

  // ==========================================
  // 4. UTILS
  // ==========================================

  const togglePlatform = (key) => {
    setSelectedPlatforms(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copied!"));
  };

  const downloadText = (text, platform) => {
    const element = document.createElement("a");
    const file = new Blob([text], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `${platform}-post.txt`;
    document.body.appendChild(element);
    element.click();
  };

  const getPlatformIcon = (key, size = 18) => {
    switch(key) {
      case 'instagram': return <Instagram size={size} />;
      case 'facebook': return <Facebook size={size} />;
      case 'linkedin': return <Linkedin size={size} />;
      case 'twitter': return <Twitter size={size} />;
      default: return <Share2 size={size} />;
    }
  };

  const getPlatformColor = (key) => {
    switch(key) {
      case 'instagram': return 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)';
      case 'facebook': return '#1877F2';
      case 'linkedin': return '#0077b5';
      case 'twitter': return '#000000'; // X branding
      default: return '#666';
    }
  };

  // ==========================================
  // 5. RENDER
  // ==========================================

  if (loadingAuth || loading || loadingKeys) {
    return (
      <div className="loader-container">
        <div className="spinner"></div>
        <p>Initializing Generator...</p>
        
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-wall">
        <div className="auth-card">
          <h2>Log in Required</h2>
          <p>Please log in to access the Social Media Generator.</p>
          <button onClick={() => window.location.href = "/login"}>Go to Login</button>
        </div>
        
      </div>
    );
  }

  return (
    <div className="premium-app-container">
      <div className="orb orb-1"></div>
      <div className="orb orb-2"></div>

      <div className="container py-5 position-relative z-1">
        <div className="d-flex justify-content-between align-items-center mb-5">
          <div className="d-flex align-items-center gap-3">
            <button className="btn btn-outline-light rounded-circle p-2 back-btn d-flex align-items-center justify-content-center" onClick={() => window.history.back()}>
              <ArrowLeft size={20} />
            </button>
            <h1 className="fw-bold m-0 text-white">
              Social Media <span className="text-neon">Post Generator</span>
            </h1>
          </div>

          <div className="credits-badge">
            <Zap size={16} fill="#eab308" color="#eab308" />
            <span>{credits} Credits</span>
            {credits < 5 && (
              <button className="btn-buy-credits" onClick={() => window.location.href = "/user/select-package"}>
                Buy More
              </button>
            )}
          </div>
        </div>

        {error && <div className="alert alert-danger glass-alert">{error}</div>}

        <div className="row g-4">
          {/* LEFT SIDEBAR - FORM */}
          <div className="col-lg-4">
            <div className="glass-card h-100 d-flex flex-column">
              <div className="card-header-glass">
                <h5 className="m-0 d-flex align-items-center gap-2 text-white">
                  <LayoutTemplate size={18} className="text-neon" />
                  Post Settings
                </h5>
              </div>
              
              <div className="card-body-glass flex-grow-1 d-flex flex-column overflow-auto">
                <div className="mb-4">
                  <label className="form-label text-light opacity-75">What is your post about? *</label>
                  <textarea 
                    className="form-control input-group-glass"
                    value={topic} 
                    onChange={(e) => setTopic(e.target.value)} 
                    placeholder="e.g. Launching our new eco-friendly coffee cups..."
                    rows={4}
                    style={{ resize: "none" }}
                  />
                </div>
                
                <div className="mb-4">
                  <label className="form-label text-light opacity-75">Target Audience (Optional)</label>
                  <input 
                    type="text" 
                    className="form-control input-group-glass"
                    value={targetAudience} 
                    onChange={(e) => setTargetAudience(e.target.value)}
                    placeholder="e.g. Gen Z, Small business owners" 
                  />
                </div>

                <div className="mb-4">
                  <label className="form-label text-light opacity-75 d-flex align-items-center gap-2">
                    <Share2 size={16} /> Platforms
                  </label>
                  <div className="d-flex flex-column gap-2">
                    {['instagram', 'facebook', 'linkedin', 'twitter'].map(platform => (
                      <div 
                        key={platform}
                        className={`d-flex align-items-center gap-3 p-2 rounded cursor-pointer border transition-all ${selectedPlatforms[platform] ? 'bg-primary bg-opacity-25 border-primary text-white' : 'border-secondary border-opacity-25 text-light opacity-75'}`}
                        onClick={() => togglePlatform(platform)}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-center rounded p-2" style={{ 
                          background: selectedPlatforms[platform] ? getPlatformColor(platform) : 'rgba(255,255,255,0.1)'
                        }}>
                          {getPlatformIcon(platform, 16)}
                        </div>
                        <span className="flex-grow-1">{platform.charAt(0).toUpperCase() + platform.slice(1)}</span>
                        {selectedPlatforms[platform] && <Check size={16} className="text-info" />}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label text-light opacity-75 d-flex align-items-center gap-2">
                    <MessageSquare size={16} /> Tone & Style
                  </label>
                  <select 
                    className="form-select input-group-glass"
                    value={tone} 
                    onChange={(e) => setTone(e.target.value)}
                  >
                    {toneOptions.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                <div className="mt-auto pt-3">
                  <button 
                    className="btn btn-neon-primary w-100 d-flex align-items-center justify-content-center gap-2 py-2" 
                    onClick={generatePosts}
                    disabled={generating || credits < 1 || !topic.trim() || Object.values(selectedPlatforms).filter(Boolean).length === 0}
                  >
                    {generating ? (
                      <><RefreshCw className="spin" size={18} /> Generating...</>
                    ) : (
                      <><Zap size={18} /> Generate Posts (1 Credit)</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDEBAR - OUTPUT */}
          <div className="col-lg-8">
            <div className="glass-card h-100 d-flex flex-column">
              <div className="card-header-glass">
                <h5 className="m-0 d-flex align-items-center gap-2 text-white">
                  <Check size={18} className={Object.keys(generatedPosts).length > 0 ? "text-success" : "text-light opacity-50"} /> 
                  Generated Content
                </h5>
              </div>
              
              <div className="card-body-glass flex-grow-1 overflow-auto position-relative" style={{ minHeight: "600px" }}>
                {generating ? (
                  <div className="h-100 d-flex flex-column align-items-center justify-content-center text-light opacity-75 py-5">
                    <div className="spinner-border text-neon mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="fs-5">Crafting viral content for your platforms...</p>
                  </div>
                ) : Object.keys(generatedPosts).length > 0 ? (
                  <div className="d-flex flex-column gap-4">
                    {Object.entries(generatedPosts).map(([platform, content]) => (
                      <div key={platform} className="glass-card overflow-hidden">
                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom border-secondary border-opacity-25" style={{ borderTop: `4px solid ${platform === 'instagram' ? '#cc2366' : platform === 'facebook' ? '#1877F2' : platform === 'linkedin' ? '#0077b5' : '#000'}` }}>
                          <div className="d-flex align-items-center gap-2 fw-bold text-white">
                            {getPlatformIcon(platform)}
                            <span>{platform.charAt(0).toUpperCase() + platform.slice(1)}</span>
                          </div>
                          <div className="d-flex gap-2">
                            <button className="btn btn-sm btn-outline-light rounded-circle p-2 d-flex align-items-center justify-content-center" onClick={() => copyToClipboard(content)} title="Copy">
                              <Copy size={14} />
                            </button>
                            <button className="btn btn-sm btn-outline-light rounded-circle p-2 d-flex align-items-center justify-content-center" onClick={() => downloadText(content, platform)} title="Download">
                              <Download size={14} />
                            </button>
                          </div>
                        </div>
                        <div className="p-4 text-white" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', background: 'rgba(0,0,0,0.2)' }}>
                          {content}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-100 d-flex flex-column align-items-center justify-content-center text-light opacity-25 py-5">
                    <LayoutTemplate size={64} className="mb-3" />
                    <h3 className="fs-4 mb-2 text-white opacity-50">Ready to Create?</h3>
                    <p className="text-center">Fill in the details on the left to generate optimized posts for multiple platforms instantly.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* STYLES */}
      
    </div>
  );
};

export default SocialMediaPostGenerator;